import { h, clear, toast, showModal, confirmModal, fold, emptyState } from '../utils/dom.js';
import { iconSvg } from '../utils/icons.js';
import { Plus, Pencil, Check, KeyRound, Copy } from 'lucide';
import { UserAPI, BranchAPI } from '../services/endpoints.js';
import { CurrentUser } from '../services/api.js';
import { validateForm, showFieldErrors, required, email, minLen, maxLen, matches } from '../utils/validators.js';
import { Avatar, AVATAR_COLORS } from '../components/avatar.js';
import { Tooltip } from '../components/tooltip.js';
import { PageTabs } from '../components/ui.js';
import { SkeletonTable } from '../components/skeleton.js';

const createUserRules = {
  full_name: [(v) => required(v, 'El nombre completo'), minLen(2, 'El nombre completo'), maxLen(100, 'El nombre completo')],
  email:     [(v) => required(v, 'El correo electrónico'), email],
  role:      [(v) => required(v, 'El rol')],
  branch_id: [(v, values) => values.role === 'admin' ? null : required(String(v), 'La sucursal')],
  document_number: [(v) => required(v, 'El número de documento')],
  phone:     [(v) => required(v, 'El teléfono')],
};

const editUserRules = {
  full_name: [(v) => required(v, 'El nombre completo'), minLen(2, 'El nombre completo'), maxLen(100, 'El nombre completo')],
  email: [(v) => required(v, 'El correo electrónico')],
  is_active: [(v) => required(String(v), 'El estado')],
  document_number: [(v) => required(v, 'El número de documento')],
  phone: [(v) => required(v, 'El teléfono')],
};

function hashCode(s) { let hash = 0; for (let i = 0; i < s.length; i++) { const ch = s.charCodeAt(i); hash = ((hash << 5) - hash) + ch; hash |= 0; } return Math.abs(hash); }

export const UsersPage = async (user) => {
  const view = document.getElementById('view');
  clear(view);
  view.appendChild(h('div', { class: 'page-title' },
    h('h1', {}, 'Usuarios'),
    (user.role === 'admin') ?
      h('button', { class: 'btn', onclick: () => openCreate() }, h('span', { html: iconSvg(Plus, 16) }), 'Nuevo usuario') : null,
  ));

  let roleFilter = '';
  let statusFilter = 'active';

  const filter = h('div', { class: 'filter-bar mb-16' },
    h('input', { type: 'search', name: 'q', 'aria-label': 'Buscar usuarios', placeholder: 'Buscar por nombre o correo…', style: { minWidth: '200px' }, oninput: applyFilters }),
    h('label', { class: 'muted text-sm' }, 'Sucursal:'),
    h('select', { name: 'branch_id', onchange: applyFilters },
      h('option', { value: '' }, 'Todas'),
    ),
    h('span', { class: 'filter-label' }, 'Rol'),
    PageTabs({
      tabs: [
        { id: '', label: 'Todos' },
        { id: 'admin', label: 'Administrador' },
        { id: 'instructor', label: 'Instructor' },
        { id: 'beneficiary', label: 'Beneficiario' },
      ],
      activeTab: roleFilter,
      onTabChange: (id) => { roleFilter = id; applyFilters(); },
    }),
    h('span', { class: 'filter-label' }, 'Estado'),
    PageTabs({
      tabs: [
        { id: '', label: 'Todos' },
        { id: 'active', label: 'Activo' },
        { id: 'inactive', label: 'Inactivo' },
      ],
      activeTab: statusFilter,
      onTabChange: (id) => { statusFilter = id; applyFilters(); },
    }),
  );
  view.appendChild(filter);

  const card = h('div', { class: 'card' });
  view.appendChild(card);

  // Show skeleton while loading
  const skeleton = h('div', { class: 'table-wrap' }, h('table', { class: 'table' },
    h('thead', {}, h('tr', {},
      h('th', {}, 'Nombre'),
      h('th', {}, 'Correo electrónico'),
      h('th', {}, 'Rol'),
      h('th', {}, 'Sucursal'),
      h('th', {}, 'Estado'),
      h('th', {}, ''),
    )),
    SkeletonTable(5, 6),
  ));
  card.appendChild(skeleton);

  let allUsers = [];
  let branches = [];

  const [brRes, userRes] = await Promise.all([
    safeAsync(() => BranchAPI.list({ include_inactive: '0' })),
    safeAsync(() => UserAPI.list()),
  ]);

  branches = (brRes?.data || brRes || []);
  if (branches.length) {
    const sel = filter.querySelector('[name="branch_id"]');
    for (const b of branches) {
      sel.appendChild(h('option', { value: b.id }, b.name));
    }
  }

  allUsers = (userRes?.data || userRes || []);
  skeleton.remove();
  applyFilters();

  async function refresh() {
    try {
      const res = await UserAPI.list();
      if (!res.data) return;
      allUsers = res.data || [];
      applyFilters();
    } catch (err) { toast(err.message, 'error'); }
  }

  function applyFilters() {
    const q = filter.querySelector('[name="q"]').value.toLowerCase().trim();
    const foldedQ = fold(q);
    const branchId = filter.querySelector('[name="branch_id"]').value;
    let items = allUsers;
    if (q) items = items.filter((u) => fold(u.full_name + ' ' + u.email).includes(foldedQ));
    if (branchId) items = items.filter((u) => String(u.branch_id) === branchId);
    if (roleFilter) items = items.filter((u) => u.role === roleFilter);
    if (statusFilter === 'active') items = items.filter((u) => u.is_active);
    else if (statusFilter === 'inactive') items = items.filter((u) => !u.is_active);
    render(card, items, user);
  }

  function render(parent, items, user) {
    clear(parent);
    if (!items.length) { parent.appendChild(emptyState('No hay usuarios que coincidan con los filtros.')); return; }
    parent.appendChild(h('div', { class: 'table-wrap' }, h('table', { class: 'table' },
      h('thead', {}, h('tr', {},
        h('th', {}, 'Nombre'),
        h('th', {}, 'Correo electrónico'),
        h('th', {}, 'Rol'),
        h('th', {}, 'Sucursal'),
        h('th', {}, 'Estado'),
        h('th', {}, ''),
      )),
      h('tbody', {}, ...items.map((u) => {
        const avatarColor = AVATAR_COLORS[hashCode(u.full_name) % AVATAR_COLORS.length];
        return h('tr', {},
          h('td', {}, h('div', { class: 'cell-with-avatar' },
            Avatar(u.full_name, avatarColor, 28),
            h('span', {}, u.full_name),
          )),
          h('td', {}, u.email),
          h('td', {}, roleBadge(u.role)),
          h('td', {}, u.branch_name || '-'),
          h('td', {}, h('span', { class: `badge ${u.is_active ? 'active' : 'inactive'}` }, u.is_active ? 'Activo' : 'Inactivo')),
          h('td', {}, h('div', { class: 'actions-cell' },
            (user.role === 'admin')
              ? Tooltip('Editar usuario', h('button', { class: 'btn-icon-only btn-edit', onclick: () => openEdit(u) }, h('span', { html: iconSvg(Pencil, 14) })))
              : null,
          )),
        );
      })))
    ));
  }

  async function openCreate() {
    const branchesRes = await BranchAPI.list({ include_inactive: '0' });
    const branches = branchesRes.data || [];

    const branchLabel = h('label', {}, 'Sucursal');
    const branchSelect = h('select', { name: 'branch_id' },
      ...branches.map((b) => h('option', { value: b.id, selected: user.branch_id === b.id ? '' : null }, b.name)),
    );
    const branchWrap = h('div', { class: 'branch-field' },
      branchLabel, branchSelect,
    );

    const roleSelect = h('select', { name: 'role', onchange: syncBranchVisibility },
      h('option', { value: 'admin' }, 'Administrador'),
      h('option', { value: 'instructor' }, 'Instructor'),
      h('option', { value: 'beneficiary' }, 'Beneficiario'),
    );
    // BranchAdmin cannot create SuperAdmin/BranchAdmin; preselect the first allowed role.
    if (user.role === 'admin' && roleSelect.firstChild) {
      roleSelect.value = roleSelect.firstChild.value;
    }

    const form = h('div', { class: 'form-create' },
      h('h3', {}, 'Nuevo usuario'),
      h('p', { class: 'muted text-sm' }, 'Se generará una contraseña temporal y se le pedirá al usuario cambiarla en su primer inicio de sesión.'),
      h('div', { class: 'form-section' },
        h('div', { class: 'grid grid-2' },
          h('div', {},
            h('label', {}, 'Nombre completo'),
            h('input', { name: 'full_name', autocomplete: 'off', required: true }),
          ),
          h('div', {},
            h('label', {}, 'Correo electrónico'),
            h('input', { name: 'email', type: 'email', autocomplete: 'off', required: true }),
          ),
        ),
        h('div', { class: 'grid grid-2' },
          h('div', {},
            h('label', {}, 'Número de documento'),
            h('input', { name: 'document_number', autocomplete: 'off', required: true }),
          ),
          h('div', {},
            h('label', {}, 'Teléfono'),
            h('input', { name: 'phone', type: 'tel', inputmode: 'numeric', autocomplete: 'tel', required: true }),
          ),
        ),
        h('label', {}, 'NIT (opcional)'),
        h('input', { name: 'nit', autocomplete: 'off' }),
      ),
      h('div', { class: 'form-section' },
        h('label', {}, 'Rol'),
        roleSelect,
        branchWrap,

      ),
      h('div', { class: 'btn-row mt-16' },
        h('button', { class: 'btn btn-block', onclick: submit }, h('span', { html: iconSvg(Plus, 16) }), 'Crear usuario'),
      ),
    );

    function syncBranchVisibility() {
      const isAdminRole = form.querySelector('[name="role"]').value === 'admin';
      branchWrap.classList.toggle('hidden', isAdminRole);
    }
    syncBranchVisibility();

    const m = showModal(form);
    form.closest('.modal')?.classList.add('modal-sheet');
    async function submit() {
      const values = {
        full_name: form.querySelector('[name="full_name"]').value.trim(),
        email: form.querySelector('[name="email"]').value.trim(),
        role: form.querySelector('[name="role"]').value,
        branch_id: form.querySelector('[name="branch_id"]')?.value,
        document_number: form.querySelector('[name="document_number"]').value.trim(),
        phone: form.querySelector('[name="phone"]').value.trim(),
      };
      const errors = validateForm(values, createUserRules);
      if (Object.keys(errors).length) { showFieldErrors(form, errors); return; }
      const role = values.role;
      const data = {
        full_name: values.full_name,
        email: values.email,
        document_number: form.querySelector('[name="document_number"]').value.trim(),
        nit: form.querySelector('[name="nit"]').value.trim() || undefined,
        phone: form.querySelector('[name="phone"]').value.trim(),
        role,
        branch_id: role === 'admin' ? undefined : Number(values.branch_id),

      };
      try {
        const res = await UserAPI.create(data);
        const temp = res.data?.temporaryPassword;
        m.close();
        if (temp) {
          showTempPasswordModal(temp, 'Usuario creado correctamente.');
        } else {
          toast('Usuario creado', 'success');
        }
        refresh();
      } catch (err) { toast(err.message, 'error'); }
    }
  }

  function openEdit(u) {
    const form = h('div', { class: 'form-create' },
      h('h3', {}, `Editar ${u.full_name}`),
      h('p', { class: 'muted text-sm' }, 'Modifica los datos del usuario.'),
      h('div', { class: 'form-section' },
        h('div', { class: 'grid grid-2' },
          h('div', {},
            h('label', {}, 'Nombre completo'),
            h('input', { name: 'full_name', value: u.full_name || '', autocomplete: 'off', required: true }),
          ),
          h('div', {},
            h('label', {}, 'Correo electrónico'),
            h('input', { name: 'email', type: 'email', value: u.email || '', autocomplete: 'email', required: true }),
          ),
        ),
        h('div', { class: 'grid grid-2' },
          h('div', {},
            h('label', {}, 'Número de documento'),
            h('input', { name: 'document_number', value: u.document_number || '', autocomplete: 'off', required: true }),
          ),
          h('div', {},
            h('label', {}, 'Teléfono'),
            h('input', { name: 'phone', type: 'tel', inputmode: 'numeric', value: u.phone || '', autocomplete: 'tel', required: true }),
          ),
        ),
        h('label', {}, 'NIT (opcional)'),
        h('input', { name: 'nit', value: u.nit || '', autocomplete: 'off' }),
      ),
      h('div', { class: 'form-section' },
        h('label', {}, 'Activo'),
        h('select', { name: 'is_active', required: true },
          h('option', { value: '1', selected: u.is_active ? '' : null }, 'Activo'),
          h('option', { value: '0', selected: !u.is_active ? '' : null }, 'Inactivo'),
        ),
      ),
      h('div', { class: 'btn-row mt-16' },
        h('button', { class: 'btn btn-block', onclick: submit }, h('span', { html: iconSvg(Check, 16) }), 'Guardar cambios'),
      ),
      (user.role === 'admin')
        ? h('div', { class: 'btn-row mt-8' },
            h('button', { class: 'btn btn-ghost btn-block', onclick: openResetPassword }, h('span', { html: iconSvg(KeyRound, 14) }), 'Restablecer contraseña'),
          )
        : null,
    );
    const m = showModal(form);
    form.closest('.modal')?.classList.add('modal-sheet');
    async function submit() {
      const values = {
        full_name: form.querySelector('[name="full_name"]').value.trim(),
        email: form.querySelector('[name="email"]').value.trim(),
        is_active: form.querySelector('[name="is_active"]').value,
        document_number: form.querySelector('[name="document_number"]').value.trim(),
        phone: form.querySelector('[name="phone"]').value.trim(),
      };
      const errors = validateForm(values, editUserRules);
      if (Object.keys(errors).length) { showFieldErrors(form, errors); return; }
      const data = {
        full_name: values.full_name,
        email: values.email,
        document_number: form.querySelector('[name="document_number"]').value.trim(),
        nit: form.querySelector('[name="nit"]').value.trim() || null,
        phone: form.querySelector('[name="phone"]').value.trim(),
        is_active: values.is_active === '1',
      };
      if (values.is_active === '0' && u.is_active) {
        const confirmed = await confirmModal(`¿Desactivar a ${u.full_name}?`, { confirmText: 'Desactivar' });
        if (!confirmed) return;
      }
      try {
        await UserAPI.update(u.id, data);
        toast('Actualizado', 'success'); m.close(); refresh();
      } catch (err) { toast(err.message, 'error'); }
    }

    function openResetPassword() {
      const form2 = h('div', { class: 'form-create' },
        h('h3', {}, `Restablecer contraseña de ${u.full_name}`),
        h('p', { class: 'muted text-sm' },
          'Deja los campos en blanco para generar una contraseña temporal aleatoria. Se le pedirá al usuario cambiarla en su próximo inicio de sesión.'
        ),
        h('div', { class: 'form-section' },
          h('label', {}, 'Nueva contraseña'),
          h('input', { name: 'newPassword', type: 'password', autocomplete: 'new-password' }),
          h('label', {}, 'Confirmar nueva contraseña'),
          h('input', { name: 'confirmPassword', type: 'password', autocomplete: 'new-password' }),
        ),
        h('div', { class: 'btn-row mt-16' },
          h('button', { class: 'btn btn-block', onclick: submitReset }, 'Restablecer contraseña'),
        ),
      );
      const m2 = showModal(form2);
      form2.closest('.modal')?.classList.add('modal-sheet');
      async function submitReset() {
        const confirmed = await confirmModal(`¿Restablecer la contraseña de ${u.full_name}?`, { confirmText: 'Restablecer' });
        if (!confirmed) return;
        const newPassword = form2.querySelector('[name="newPassword"]').value;
        const confirmPassword = form2.querySelector('[name="confirmPassword"]').value;
        const errors = validateForm(
          { newPassword, confirmPassword },
          {
            newPassword:     [(v) => v ? minLen(6, 'La contraseña')(v) : null],
            confirmPassword: [(v) => v ? matches('newPassword', 'La confirmación no coincide')(v, { newPassword }) : null],
          },
        );
        if (Object.keys(errors).length) { showFieldErrors(form2, errors); return; }
        try {
          const res = await UserAPI.resetPassword(u.id, newPassword || undefined);
          const temp = res.data?.temporaryPassword;
          m2.close();
          m.close();
          refresh();
          if (temp) {
            const result = h('div', {},
              h('h3', {}, 'Contraseña restablecida'),
              h('p', {}, 'Se ha generado una nueva contraseña temporal. Compártela con el usuario de forma segura:'),
              h('div', { class: 'card', style: { background: 'var(--c-panel-2)', textAlign: 'center', fontFamily: 'monospace', fontSize: '16px', letterSpacing: '1px' } },
                h('strong', {}, temp),
              ),
              h('p', { class: 'muted text-sm mt-8' },
                'Se le pedirá al usuario cambiar esta contraseña en su próximo inicio de sesión.'
              ),
              h('div', { class: 'btn-row mt-16' },
                h('button', { class: 'btn', onclick: () => { navigator.clipboard?.writeText(temp); toast('Copiado al portapapeles', 'success'); } }, h('span', { html: iconSvg(Copy, 14) }), 'Copiar'),
                h('button', { class: 'btn btn-ghost', onclick: () => resultModal.close() }, 'Cerrar'),
              ),
            );
            const resultModal = showModal(result);
          } else {
            toast('Contraseña actualizada', 'success');
          }
        } catch (err) { toast(err.message, 'error'); }
      }
    }
  }
};

const roleLabel = (r) => r === 'admin' ? 'administrador' : r === 'instructor' ? 'instructor' : 'beneficiario';
const roleBadge = (r) => {
  const map = { admin: 'danger', instructor: 'info', beneficiary: 'success' };
  return h('span', { class: `badge ${map[r] || 'info'}` }, roleLabel(r));
};

const showTempPasswordModal = (password, message) => {
  const content = h('div', {},
    h('h3', {}, 'Contraseña temporal generada'),
    h('p', {}, message || 'Compartí esta contraseña temporal de forma segura con el usuario:'),
    h('div', { class: 'card', style: { background: 'var(--c-panel-2)', textAlign: 'center', fontFamily: 'monospace', fontSize: '16px', letterSpacing: '2px', padding: '16px' } },
      h('strong', {}, password),
    ),
    h('p', { class: 'muted text-sm mt-8' }, 'Se le pedirá al usuario cambiarla en su próximo inicio de sesión.'),
    h('div', { class: 'btn-row mt-16' },
      h('button', { class: 'btn', onclick: () => { navigator.clipboard?.writeText(password); toast('Copiado al portapapeles', 'success'); } }, h('span', { html: iconSvg(Copy, 14) }), 'Copiar'),
      h('button', { class: 'btn btn-ghost', onclick: () => pwdModal.close() }, 'Cerrar'),
    ),
  );
  const pwdModal = showModal(content);
};

async function safeAsync(fn) {
  try { const r = await fn(); return r?.data || r || []; } catch { return []; }
}

